import uuid from '../../lib/uuid';
import { normalizeError } from '../utils';
class Analytics {
    _queue = [];
    _options = {};
    _waitForBufferingCheck = null;
    listeners = {};
    maxAttempts = 5;
    timeReportInterval = 10000;
    player;
    lastReportAt = null;
    lastPlayedAt = null;
    lastBufferStart = null;
    isAttaching = false;
    isPlaying = false;
    isBuffering = false;
    duration = 0;
    durationPlaying = 0;
    activeBufferingDuration = 0;
    totalDurationBuffering = 0;
    viewId = '';
    viewerId = '';
    isSetup = false;
    stoppedHACK = false;
    constructor(options) {
        this.updateOptions(options);
    }
    updateOptions(options = {}) {
        this._options = { ...options };
        this.maxAttempts = this._options.maxAttempts || 5;
        this.timeReportInterval = this._options.timeInterval !== undefined ? this._options.timeInterval : 10000;
    }
    attach({ video, viewerId }) {
        if (Object.keys(this.listeners).length > 0) {
            this.detach();
        }
        this.player = video;
        this.lastReportAt = null;
        this.lastPlayedAt = null;
        this.lastBufferStart = null;
        this.isAttaching = false;
        this.isPlaying = false;
        this.isBuffering = false;
        this.duration = 0;
        this.durationPlaying = 0;
        this.activeBufferingDuration = 0;
        this.totalDurationBuffering = 0;
        this.viewerId = viewerId;
        this.viewId = uuid();
        this.isSetup = false;
        this.listeners = this._wireEvents(this.player);
    }
    detach() {
        const keys = Object.keys(this.listeners);
        const isAttached = keys.length > 0;
        keys.forEach((key) => {
            const event = key;
            if (event === 'visibilitychange') {
                const listener = this.listeners[event];
                listener && document?.removeEventListener(event, listener, false);
            }
            else if (event === 'pagehide') {
                const listener = this.listeners[event];
                listener && window?.removeEventListener(event, listener, false);
            }
            else {
                const listener = this.listeners[event];
                listener && this.player?.removeEventListener(event, listener, false);
            }
        });
        this.listeners = {};
        this._waitForBufferingCheck && clearTimeout(this._waitForBufferingCheck);
        if (isAttached) {
            this._report('detach');
        }
        return this;
    }
    _wireEvents(player) {
        const listeners = {
            ended: async () => {
                this._handleNormalOperation();
                await this._report('complete');
                this._handleBufferingEnd();
            },
            error: async () => {
                await this._handlePlaybackError(this.player?.error);
            },
            pause: async () => {
                this._handleNormalOperation();
                await this._report('pause');
                this.isPlaying = false;
                this._handleBufferingEnd();
            },
            play: async () => {
                this._handleNormalOperation();
                await this._report('play');
                this.isPlaying = true;
                this._handleBufferingEnd();
            },
            playing: () => {
                this._handleNormalOperation();
                this.isPlaying = true;
                this.isAttaching = true;
                this._handleBufferingEnd();
            },
            resize: async () => {
                this._handleNormalOperation();
                await this._report('quality');
                this._handleBufferingEnd();
            },
            seeking: async () => {
                this._handleNormalOperation();
                await this._report('seek');
            },
            seeked: () => {
                this._handleNormalOperation();
                this._handleBufferingEnd();
            },
            timeupdate: async () => {
                await this._reportTime();
            },
            stalled: () => {
                this._handleBufferingStart();
            },
            waiting: () => {
                this.isPlaying = false;
                this._handleBufferingStart();
            },
            visibilitychange: async () => {
                await this._report('visibilitychange');
            },
            pagehide: async (event) => {
                if (!event.persisted) {
                    this._report('pagehide');
                }
            },
        };
        Object.keys(listeners).forEach((key) => {
            const event = key;
            if (event === 'visibilitychange') {
                document.addEventListener(event, listeners[event], false);
            }
            else if (event === 'pagehide') {
                window.addEventListener(event, listeners[event], false);
            }
            else {
                player.addEventListener(event, listeners[event], false);
            }
        });
        return listeners;
    }
    _isActuallyPlaying() {
        if (!this.player)
            return false;
        return this.player.currentTime > 0 && !this.player.paused && !this.player.ended && this.player.readyState > 2;
    }
    _getCurrentTime() {
        return this.player?.currentTime || 0;
    }
    _getVideoDuration() {
        return this.player?.duration || 0;
    }
    _getCurrentLevelHeight() {
        return this.player?.videoHeight || 0;
    }
    _handleBufferingStart() {
        this.isBuffering = true;
        this.lastBufferStart = this.lastBufferStart || new Date();
        if (this._waitForBufferingCheck) {
            return;
        }
        this._waitForBufferingCheck = setTimeout(async () => {
            this._waitForBufferingCheck = null;
            if (!this.isBuffering) {
                return;
            }
            if (this._isActuallyPlaying()) {
                this._handleBufferingEnd();
                return;
            }
            await this._report('buffer');
        }, 500);
    }
    _handleNormalOperation() {
        this.stoppedHACK = false;
    }
    _handleBufferingEnd() {
        this.isBuffering = false;
        this.lastBufferStart = null;
        this._waitForBufferingCheck && clearTimeout(this._waitForBufferingCheck);
        this._waitForBufferingCheck = null;
        this.totalDurationBuffering += this.activeBufferingDuration;
        this.activeBufferingDuration = 0;
    }
    async _handlePlaybackError(error = null) {
        if (this.stoppedHACK) {
            console.warn('An error occurred, but playback is stopped so this should not be a problem', error);
        }
        else if (error === null) {
            console.warn('An error occurred, but the error was null');
        }
        else {
            await this._report('error', { error: normalizeError(error) });
        }
    }
    async _reportTime() {
        if (!this.isSetup || !this.isAttaching) {
            return;
        }
        const n = new Date();
        if (this.lastReportAt && +n - +this.lastReportAt <= this.timeReportInterval) {
            return;
        }
        await this._report('time');
    }
    async _report(action, params) {
        if (!this.isSetup) {
            this.isSetup = true;
            await this._report('setup');
        }
        const n = new Date();
        const durationDelta = this.isAttaching ? +n - +(this.lastReportAt || n) : 0;
        const durationPlayingDelta = this.isPlaying ? +n - +(this.lastReportAt || n) : 0;
        const durationBufferingDelta = this.isBuffering ? +n - +(this.lastBufferStart || n) : 0;
        this.duration += durationDelta;
        this.durationPlaying += durationPlayingDelta;
        this.activeBufferingDuration += durationBufferingDelta;
        this.isAttaching = true; //
        this.lastReportAt = n;
        const metrics = {
            ...params,
            action,
            viewerId: this.viewerId,
            viewId: this.viewId,
            videoId: this._options.videoId,
            userData: this._options.userData ? JSON.parse(JSON.stringify(this._options.userData)) : undefined,
            timestamp: n.toISOString(),
            hourOfDay: n.getHours(),
            dayOfWeek: n.getDay(),
            position: this._getCurrentTime(),
            videoDuration: this._getVideoDuration(),
            durationDelta: Math.round(durationDelta / 1000),
            duration: Math.round(this.duration / 1000),
            durationPlayingDelta: Math.round(durationPlayingDelta / 1000),
            durationPlaying: Math.round(this.durationPlaying / 1000),
            durationBufferingDelta: Math.round(durationBufferingDelta / 1000),
            durationBuffering: Math.round((this.totalDurationBuffering + this.activeBufferingDuration) / 1000),
            videoHeight: this._getCurrentLevelHeight(),
        };
        this._queue.push(metrics);
        await this._options.onQueue?.(metrics);
        await this._dequeue();
    }
    async _dequeue() {
        const requeue = [];
        for (const metrics of this._queue) {
            try {
                await this._options.send?.(metrics);
            }
            catch (e) {
                const error = normalizeError(e);
                metrics.error = error;
                await this._options.onError?.(metrics);
                metrics.__attempts = (metrics.__attempts || 0) + 1;
                if (metrics.__attempts <= this.maxAttempts) {
                    console.warn('Unable to post metrics; will retry', error, metrics);
                    requeue.push(metrics);
                    await this._options.onRequeue?.(metrics);
                }
                else {
                    console.warn('Unable to post metrics; will not retry', error, metrics);
                    await this._options.onFail?.(metrics);
                }
            }
        }
        this._queue = requeue;
    }
}
export default Analytics;
